import { BOOKKEEPING_V2 } from "constants/addons";
import {
  SUPPORT_EXECUTIVE,
  SUPPORT_TEAM,
  TAX_PREPARER,
  BOOKKEEPING_EXPERT,
  CEO,
  ACCOUNT_MANAGER,
} from "constants/help";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useModal } from "hooks/useModal";
import { useGetEntitiesQuery } from "store/apis/group";
import { useGetSubscriptionsQuery } from "store/apis/subscriptions";
import { useGetTaskListForUserQuery } from "store/apis/taskList";
import { HelpCard } from "./HelpCard";
import dayjs from "dayjs";
import { pluralize } from "utils/pluralize";
import { useGetChatSettingsQuery } from "store/apis/chatSettings";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import { Banner } from "components/DesignSystem/Banner/Banner";
import { USER_PROFILE } from "constants/userTypes";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { PlatformTourHelpItem } from "components/PlatformTour/PlatformTourHelpItem";

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

export type AvailableAgentsListProps = {
  available_end_time?: string;
  available_start_time?: string;
  is_available?: boolean;
  name?: string;
  profile_url?: string;
  type?: "ACCOUNT_EXECUTIVE" | "SALES_EXECUTIVE" | "CALL_EXECUTIVE";
  uuid?: string;
};

export const HelpContainer = ({
  setOpen,
  agentCallingModals,
  setModalType,
  serviceModal,
  isSupportTeamAgentAvaliable = true,
  entityIdFromParent,
}: {
  availableAgentsList?: AvailableAgentsListProps[];
  setOpen: (open: boolean) => void;
  agentCallingModals?: ReturnType<typeof useModal>;
  setModalType: (type: string) => void;
  serviceModal?: ReturnType<typeof useModal>;
  isSupportTeamAgentAvaliable?: boolean;
  entityIdFromParent?: string;
}) => {
  const { data: { groups: [group] = [null] } = {} } = useGetEntitiesQuery();
  const assignedAgent = group?.assigned_agent;
  const isAvailable = Boolean(assignedAgent?.is_available);
  const { uuid: groupId } = useCurrentGroupContext();
  const currentEntityId = useCurrentEntityId();
  const entityId = currentEntityId || entityIdFromParent;
  const firstEntityId = group?.entities?.[0]?.uuid;
  const { data: chatSettings } = useGetChatSettingsQuery();
  const holidays = chatSettings?.holidays;
  const isHolidayToday = holidays?.some((holiday) =>
    dayjs(holiday.date).isSame(dayjs(), "day")
  );
  const { isForeignCA, isUserTypeForeignCA } = useRoleBasedView();
  const isFCA = isUserTypeForeignCA || isForeignCA;

  const getAvailableInTime = () => {
    const startTime = assignedAgent?.available_start_time;
    if (!startTime) return "Available soon";

    const now = dayjs().utcOffset(330);
    let start = dayjs
      .utc(startTime, "HH:mm:ss")
      .utcOffset(330)
      .set("year", now.year())
      .set("month", now.month())
      .set("date", now.date());

    if (now.isAfter(start)) start = start.add(1, "day");

    const diff = start.diff(now, "minute");
    const hrs = Math.floor(diff / 60);
    const mins = diff % 60;

    if (diff <= 0) return "Available soon";
    if (hrs && mins)
      return `Available in ${pluralize(hrs, "hour", "hours")} ${pluralize(
        mins,
        "minute",
        "minutes"
      )}`;
    if (hrs) return `Available in ${pluralize(hrs, "hour", "hours")}`;
    return `Available in ${pluralize(mins, "minute", "minutes")}`;
  };

  const isSupportTeamUnavailable = (() => {
    const currentTimeIST = dayjs().tz("Asia/Kolkata");

    const startTimeIST = dayjs().tz("Asia/Kolkata").hour(2).minute(0).second(0);

    const endTimeIST = dayjs().tz("Asia/Kolkata").hour(6).minute(30).second(0);
    return (
      currentTimeIST.isSameOrAfter(startTimeIST) &&
      currentTimeIST.isSameOrBefore(endTimeIST)
    );
  })();

  const isAgentAvailable = (() => {
    const currentTimeIST = dayjs().tz("Asia/Kolkata");

    const nowIST = dayjs().tz("Asia/Kolkata");

    const startTimeUTC = dayjs.utc(
      assignedAgent?.available_start_time,
      "HH:mm:ss"
    );
    const startTimeIST = startTimeUTC
      .set("year", nowIST.year())
      .set("month", nowIST.month())
      .set("date", nowIST.date())
      .tz("Asia/Kolkata");

    const endTimeUTC = dayjs.utc(assignedAgent?.available_end_time, "HH:mm:ss");
    const endTimeIST = endTimeUTC
      .set("year", nowIST.year())
      .set("month", nowIST.month())
      .set("date", nowIST.date())
      .tz("Asia/Kolkata");

    if (!startTimeIST.isValid() || !endTimeIST.isValid()) {
      return false;
    }

    return (
      currentTimeIST.isSameOrAfter(startTimeIST) &&
      currentTimeIST.isSameOrBefore(endTimeIST)
    );
  })();

  const { data: platformSubscriptions = [] } = useGetSubscriptionsQuery(
    {
      groupId,
      entityId: firstEntityId,
      subscription_types: "PLATFORM",
    },
    { skip: !groupId || !firstEntityId }
  );

  const hasTaxStandardPlan = platformSubscriptions.some(
    (s) => s.upgrade_level >= 1 && (s.group_subscription?.length ?? 0) > 0
  );

  const { data: task1120List } = useGetTaskListForUserQuery(
    { baseTaskKey: "FORM_1120_US_CORPORATION_INCOME_TAX" },
    { skip: hasTaxStandardPlan }
  );
  const showTaxPreparerCard =
    hasTaxStandardPlan || Boolean(task1120List?.tasks?.length);

  const { data: bookkeepingSubscriptionPlans = [] } = useGetSubscriptionsQuery(
    {
      groupId,
      subscription_types: BOOKKEEPING_V2,
    },
    { skip: !groupId }
  );

  const allSubscriptions = bookkeepingSubscriptionPlans?.filter(
    (item) => item?.group_subscription?.length
  );

  const isBookDisabled = !allSubscriptions.length;

  const hasAssignedAgent = Boolean(assignedAgent);
  const isAssignAgentActive = isAvailable && isAgentAvailable;

  const isSPOCOnline =
    hasAssignedAgent && !isHolidayToday && isAssignAgentActive;
  const spocInTimeRange =
    hasAssignedAgent && !isHolidayToday && isAgentAvailable;

  const shouldShowSPOCAvailableIn = !isSPOCOnline;
  const shouldShowSPOCCallTooltip = !hasAssignedAgent || !isAvailable;

  const spocAvailableText = hasAssignedAgent
    ? getAvailableInTime()
    : "Not assigned yet";

  const spocModalText = assignedAgent?.name
    ? `Call ${assignedAgent?.name}`
    : "Call Support Executive";

  const spocName = assignedAgent?.name || "";

  const isSupportTeamAvailable =
    !isHolidayToday && !isSupportTeamUnavailable && isSupportTeamAgentAvaliable;

  const shouldShowSupportTeamAvailableIn = !isSupportTeamAgentAvaliable;
  const shouldDisableSupportTeamCall =
    isSupportTeamUnavailable || isHolidayToday;

  const supportTeamAvailableText = shouldShowSupportTeamAvailableIn
    ? getAvailableInTime()
    : "";

  const taxPreparerName = "Schedule call with a tax expert";

  const bookkeepingExpertName = isBookDisabled
    ? "An active bookkeeping service is required to enable a call with our bookkeeping expert"
    : "Schedule call with a bookkeeping expert";

  const handleBooksDisabled = () => {
    serviceModal?.open();
    setOpen(false);
  };

  const handleCloseHelp = () => setOpen(false);

  const supportNote = (() => {
    if (isHolidayToday) {
      return "Our team is currently unavailable due to a holiday. Please reach out later.";
    }

    if (isSupportTeamUnavailable) {
      return "Our team is currently offline. Feel free to book a time slot via “Schedule” or leave a message in chat.";
    }

    return "";
  })();

  return (
    <section className="t-flex t-flex-col t-gap-3.5">
      {supportNote && <Banner type="info" description={supportNote} />}
      <PlatformTourHelpItem onSelect={handleCloseHelp} />
      <div className="t-grid t-gap-3 t-grid-cols-1">
        {hasAssignedAgent && isSPOCOnline ? (
          <div className="t-flex-1">
            {/* Support Executive Card */}
            <HelpCard
              type={ACCOUNT_MANAGER}
              isOnline={isSPOCOnline}
              isInTimeRange={spocInTimeRange}
              profileImage={assignedAgent?.profile_url || USER_PROFILE}
              name={spocName}
              showCall={isSPOCOnline}
              availableIn={spocAvailableText}
              showAvailableIn={shouldShowSPOCAvailableIn}
              setOpen={setOpen}
              agentCallingModals={agentCallingModals}
              setModalType={() => setModalType(spocModalText)}
              showCallTooltip={shouldShowSPOCCallTooltip}
              actionOrientation="vertical"
              showSchedule={false}
              customTooltipMessage={
                !hasAssignedAgent ? "Account manager not assigned" : undefined
              }
            />
          </div>
        ) : (
          <div className="t-flex-1">
            {/* Support Team Card */}
            <HelpCard
              type={SUPPORT_TEAM}
              showSchedule
              isOnline={isSupportTeamAvailable}
              showCall
              availableIn={supportTeamAvailableText}
              showAvailableIn={shouldShowSupportTeamAvailableIn}
              setOpen={setOpen}
              agentCallingModals={agentCallingModals}
              setModalType={() => setModalType("Call Inkle Support")}
              showDisabledCall={shouldDisableSupportTeamCall}
              actionOrientation="vertical"
              customTooltipMessage={
                !isSupportTeamAvailable
                  ? "Support team is offline. Send a message in chat"
                  : undefined
              }
              showCallTooltip={!isSupportTeamAvailable}
            />
          </div>
        )}
      </div>
      {!isFCA && (
        <>
          {/* Tax Preparer Card */}
          {showTaxPreparerCard && (
            <HelpCard
              type={TAX_PREPARER}
              isOnline={false}
              name={taxPreparerName}
              setOpen={setOpen}
              agentCallingModals={agentCallingModals}
              showIcon={false}
            />
          )}
          {/* Bookkeeping Expert Card */}
          <HelpCard
            type={BOOKKEEPING_EXPERT}
            isOnline={false}
            name={bookkeepingExpertName}
            setOpen={setOpen}
            agentCallingModals={agentCallingModals}
            disabledCall={handleBooksDisabled}
            isDisabled={isBookDisabled}
            showIcon={false}
          />
          {/* CEO Card */}
          <HelpCard
            type={CEO}
            isOnline
            isInTimeRange
            name="Anand Krishna"
            showDivider
            showCall
            setOpen={setOpen}
            agentCallingModals={agentCallingModals}
            setModalType={() => setModalType("Call CEO")}
          />
        </>
      )}
    </section>
  );
};
